import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  X,
} from 'lucide-react';

import {
  formatCountdownLabel,
  getPublicBookingPhoneVerificationErrorMessage,
  getPublicBookingPhoneVerificationFeedbackTitle,
  type PhoneVerificationFeedbackTone,
} from '@/components/public/visits/publicBookingPhoneVerificationUx';
import PhoneInput from '@/components/PhoneInput';
import { usePublicBookingPhoneVerificationFlow } from '@/hooks/public/visits/usePublicBookingPhoneVerificationFlow';
import { cn } from '@/lib/utils';

interface PublicAuthenticatedPhoneVerificationCardProps {
  userId?: string | null;
  onVerified?: () => Promise<void> | void;
  className?: string;
  showSummaryCard?: boolean;
  autoOpen?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  title?: string;
  description?: string;
  successTitle?: string;
  successDescription?: string;
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

interface PrimaryButtonProps {
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  isSuccess?: boolean;
  icon?: typeof ArrowRight;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

function PrimaryButton({
  label,
  disabled = false,
  isLoading = false,
  isSuccess = false,
  icon: Icon,
  onClick,
  className = '',
}: PrimaryButtonProps) {
  const [ripples, setRipples] = useState<Array<{ x: number; y: number; id: number }>>([]);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading || isSuccess) return;

    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      setRipples((current) => [
        ...current,
        { x: event.clientX - rect.left, y: event.clientY - rect.top, id: Date.now() },
      ]);
      window.setTimeout(() => {
        setRipples((current) => current.slice(1));
      }, 700);
    }

    onClick?.(event);
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      disabled={disabled || isLoading || isSuccess}
      className={cn(
        'relative flex h-[3.8rem] w-full select-none items-center justify-center overflow-hidden rounded-full px-8 text-[11px] font-bold uppercase tracking-widest transition-all duration-300 outline-none',
        (disabled || isLoading)
          ? 'cursor-not-allowed bg-slate-100 text-slate-400 shadow-none dark:bg-zinc-900 dark:text-zinc-600'
          : isSuccess
            ? 'bg-emerald-500 text-white shadow-emerald-500/30'
            : 'bg-emerald-600 text-white shadow-[0_4px_14px_0_rgba(16,185,129,0.39)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(16,185,129,0.23)] active:scale-[0.98]',
        className,
      )}
    >
      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="pointer-events-none absolute h-5 w-5 rounded-full bg-white/40 animate-[identity-verification-ripple_0.7s_ease-out]"
          style={{ left: ripple.x, top: ripple.y, transform: 'translate(-50%, -50%)' }}
        />
      ))}
      <div className="relative z-10 flex items-center gap-3">
        {isLoading ? (
          <div className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
        ) : isSuccess ? (
          <CheckCircle2 size={20} className="animate-in zoom-in duration-300" />
        ) : (
          Icon ? <Icon size={18} strokeWidth={2} /> : null
        )}
        <span>{isLoading ? 'Verificando...' : isSuccess ? '¡Identidad validada!' : label}</span>
      </div>
    </button>
  );
}

interface OtpInputGroupProps {
  length?: number;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
}

function OtpInputGroup({ length = 6, value, disabled = false, onChange, onComplete }: OtpInputGroupProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const text = event.target.value;
    if (!/^[0-9]*$/.test(text)) return;

    const next = Array.from({ length }, (_, currentIndex) => value[currentIndex] || '');
    next[index] = text.slice(-1);
    const joined = next.join('');

    onChange(joined);

    if (text !== '' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (isCompleteOtpValue(joined, length) && onComplete) {
      onComplete(joined);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text/plain').slice(0, length).replace(/[^0-9]/g, '');
    if (!pastedData) return;

    onChange(pastedData);
    const focusIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[focusIndex]?.focus();

    if (isCompleteOtpValue(pastedData, length) && onComplete) {
      onComplete(pastedData);
    }
  };

  return (
    <div className="my-8 flex justify-center gap-1.5 sm:gap-2.5" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => {
        const char = value[index] || '';
        const isFilled = char !== '';

        return (
          <input
            key={index}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            disabled={disabled}
            value={char}
            onChange={(event) => handleChange(event, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'h-12 w-10 rounded-[0.85rem] text-center text-xl font-bold outline-none transition-all duration-200 sm:h-14 sm:w-12',
              isFilled
                ? 'scale-[1.02] border border-slate-900 bg-slate-900 text-white shadow-md dark:border-emerald-400 dark:bg-emerald-500 dark:text-black'
                : 'border border-slate-200 bg-white text-slate-900 hover:border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-800 dark:bg-zinc-950 dark:text-white dark:hover:border-zinc-700',
              disabled ? 'cursor-not-allowed opacity-70' : '',
            )}
            aria-label={`Dígito ${index + 1}`}
          />
        );
      })}
    </div>
  );
}

function isCompleteOtpValue(value: string, length: number): boolean {
  return value.length === length && /^\d+$/.test(value);
}

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 640;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

function FeedbackBanner({ tone, message }: { tone: PhoneVerificationFeedbackTone; message: string }) {
  const toneClasses = {
    error: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200',
    info: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200',
  } as const;

  return (
    <div className={cn('rounded-[1.25rem] border px-4 py-3 text-sm', toneClasses[tone])}>
      <p className="font-semibold">{getPublicBookingPhoneVerificationFeedbackTitle(tone)}</p>
      <p className="mt-1 leading-relaxed">{message}</p>
    </div>
  );
}

export function PublicAuthenticatedPhoneVerificationCard({
  userId,
  onVerified,
  className,
  showSummaryCard = true,
  autoOpen = false,
  dismissible = true,
  onDismiss,
  title = 'Confirma tu WhatsApp',
  description = 'Lo hacemos una sola vez para cuidar tu cuenta y dejar listas tus próximas visitas.',
  successTitle = 'Tu cuenta quedó protegida',
  successDescription = 'Ya puedes seguir adelante sin volver a pasar por esta validación en tus próximas visitas.',
  secondaryAction,
}: PublicAuthenticatedPhoneVerificationCardProps) {
  const isMobile = useIsMobileViewport();
  const [isOpen, setIsOpen] = useState(false);
  const [deliveryStartedInCurrentOpen, setDeliveryStartedInCurrentOpen] = useState(false);
  const controller = usePublicBookingPhoneVerificationFlow({
    enabled: Boolean(userId),
    userId,
    onVerified: async () => {
      await onVerified?.();
      setIsOpen(false);
    },
  });
  const { clearFeedback, hasVerifiedPhone } = controller;
  const showCelebrationState = controller.feedback?.tone === 'success';

  useEffect(() => {
    if (hasVerifiedPhone) {
      setIsOpen(false);
    }
  }, [hasVerifiedPhone]);

  useEffect(() => {
    if (!autoOpen || hasVerifiedPhone || isOpen) {
      return;
    }

    clearFeedback();
    setDeliveryStartedInCurrentOpen(false);
    setIsOpen(true);
  }, [autoOpen, clearFeedback, hasVerifiedPhone, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!dismissible || controller.isBusy) {
      return;
    }

    onDismiss?.();
    setDeliveryStartedInCurrentOpen(false);
    setIsOpen(false);
  };

  const otpDeliveryHeadline = deliveryStartedInCurrentOpen
    ? 'Enviamos un código a'
    : 'Ya tienes una validación activa en';

  const otpHelperText = deliveryStartedInCurrentOpen
    ? 'Escribe los 6 dígitos que acabamos de mandar por WhatsApp para continuar con tu reserva.'
    : 'Si ya recibiste el código, escríbelo aquí. Si no lo ves, puedes reenviarlo desde esta misma pantalla.';

  const primaryLabel =
    controller.stateQuery.isLoading
      ? 'Preparando...'
      : controller.stateQuery.isError
        ? 'Intentar de nuevo'
        : controller.verificationStage === 'success'
          ? 'Listo'
          : controller.verificationStage === 'otp'
            ? 'Verificar y continuar'
            : controller.hasPendingPhone
              ? 'Enviar código a este WhatsApp'
              : 'Enviar código y continuar';

  if (controller.stateQuery.isLoading && showSummaryCard) {
    return (
      <div className={cn('rounded-[1.8rem] border border-slate-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-950', className)}>
        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-zinc-400">
          <Loader2 className="size-4 animate-spin" />
          Revisando el estado de tu confirmación de identidad...
        </div>
      </div>
    );
  }

  if (controller.stateQuery.isError && showSummaryCard) {
    return (
      <div className={cn('rounded-[1.8rem] border border-rose-200 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10', className)}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">No pudimos preparar la confirmación de identidad</p>
            <p className="mt-1 text-sm text-rose-700 dark:text-rose-200">
              {getPublicBookingPhoneVerificationErrorMessage(controller.stateQuery.error)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void controller.stateQuery.refetch()}
            className="inline-flex h-11 items-center justify-center rounded-full border border-rose-200 px-4 text-sm font-medium text-rose-700 transition-colors hover:bg-white dark:border-rose-400/30 dark:text-rose-200 dark:hover:bg-rose-500/10"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const modalContent = (
    <>
      <button
        type="button"
        aria-label="Cerrar confirmación de identidad"
        className={cn(
          'fixed inset-0 z-[10020] bg-slate-950/74 backdrop-blur-md transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleClose}
      />

      <div
        className={cn(
          'pointer-events-none fixed inset-0 z-[10030] flex',
          isMobile ? 'items-end justify-stretch' : 'items-center justify-center p-4 sm:p-6',
        )}
      >
        <div
          className={cn(
            'pointer-events-auto relative w-full',
            isMobile
              ? isOpen
                ? 'h-[100dvh] translate-y-0 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]'
                : 'h-[100dvh] translate-y-full transition-transform duration-500 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]'
              : isOpen
                ? 'max-h-[min(92vh,860px)] max-w-[min(92vw,540px)] scale-100 opacity-100 transition-all duration-300'
                : 'max-h-[min(92vh,860px)] max-w-[min(92vw,540px)] scale-95 opacity-0 transition-all duration-300',
          )}
        >
          <div className={cn('relative flex flex-col overflow-hidden bg-white shadow-[0_38px_140px_-30px_rgba(2,6,23,0.78)] ring-1 ring-slate-950/8 dark:bg-black dark:ring-zinc-800', isMobile ? 'h-full rounded-t-[2rem]' : 'max-h-[min(92vh,860px)] rounded-[2rem] border border-white/80 dark:border-zinc-800')}>
            <div className="flex items-center justify-between px-6 pb-2 pt-6">
              {isMobile ? (
                <div className="absolute left-1/2 top-3 h-1.5 w-12 -translate-x-1/2 rounded-full bg-slate-200 dark:bg-zinc-800" />
              ) : (
                <div />
              )}
              {dismissible ? (
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={controller.isBusy}
                  className={cn(
                    'ml-auto rounded-full bg-slate-50 p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white',
                    isMobile ? 'mt-2' : '',
                    controller.isBusy ? 'pointer-events-none opacity-0' : 'opacity-100',
                  )}
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              ) : null}
            </div>

            <div className="flex flex-1 flex-col justify-center overflow-y-auto px-6 pb-6 sm:px-10">
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-sm ring-4 ring-emerald-50/50 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/10">
                  {controller.stateQuery.isLoading ? (
                    <Loader2 size={28} className="animate-spin" />
                  ) : controller.stateQuery.isError ? (
                    <RefreshCw size={28} strokeWidth={1.5} />
                  ) : (
                    <>
                      <MessageCircle size={28} strokeWidth={1.5} />
                      <div className="absolute ml-8 mt-8 rounded-full bg-white p-0.5 shadow-sm dark:bg-zinc-950 dark:ring-1 dark:ring-zinc-800">
                        <ShieldCheck size={14} className="text-emerald-500" />
                      </div>
                    </>
                  )}
                </div>

                <h2 className="mb-3 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                  {controller.stateQuery.isLoading
                    ? 'Estamos preparando tu verificación'
                    : controller.stateQuery.isError
                      ? 'No pudimos abrir la verificación'
                      : controller.verificationStage === 'success'
                        ? successTitle
                        : title}
                </h2>
                <p className="mx-auto max-w-[320px] text-sm leading-relaxed text-slate-500 dark:text-zinc-400">
                  {controller.stateQuery.isLoading
                    ? 'Espera un momento mientras revisamos el estado de tu cuenta.'
                    : controller.stateQuery.isError
                      ? getPublicBookingPhoneVerificationErrorMessage(controller.stateQuery.error)
                      : controller.verificationStage === 'success'
                        ? successDescription
                        : description}
                </p>
              </div>

              {controller.stateQuery.isLoading ? null : controller.stateQuery.isError ? null : (
                <>
                  {controller.feedback ? (
                    <div className="mt-6">
                      <FeedbackBanner tone={controller.feedback.tone} message={controller.feedback.message} />
                    </div>
                  ) : null}

                  {controller.verificationStage === 'phone' ? (
                    <div className="mt-8 space-y-4">
                      {controller.hasPendingPhone ? (
                        <div className="rounded-[1.25rem] border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sm text-sky-900 dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-100">
                          <p className="font-semibold">Ya hay un número pendiente por confirmar</p>
                          <p className="mt-1 leading-relaxed">
                            {controller.state?.securityPhonePendingMasked ?? 'Tu WhatsApp pendiente'} sigue pendiente de validación. El código solo se enviará cuando confirmes con el botón de abajo.
                          </p>
                        </div>
                      ) : null}

                      <div className="text-center text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                        <span>Celular de contacto</span>
                      </div>

                      <PhoneInput
                        value={controller.phone}
                        onChange={(value) => controller.setPhone(value)}
                        selectedCountry={controller.selectedCountry}
                        onCountryChange={controller.setSelectedCountry}
                        placeholder={controller.selectedCountry.format ?? 'Número de WhatsApp'}
                        disabled={controller.isBusy}
                        showValidation
                        className="mx-auto w-full max-w-[28rem]"
                      />

                      <p className="text-center text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                        Usaremos este número con su lada internacional para enviarte un código por WhatsApp y dejar tu cuenta lista para agendar con tranquilidad.
                      </p>
                    </div>
                  ) : null}

                  {controller.verificationStage === 'otp' ? (
                    <div className="mb-2 mt-8">
                      <div className="mb-2 flex items-center justify-center gap-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">
                        <span>{otpDeliveryHeadline}</span>
                        <span className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1 text-slate-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200">
                          {controller.maskedDeliveryTarget ?? 'tu WhatsApp'}
                        </span>
                      </div>

                      <p className="mx-auto max-w-[340px] text-center text-xs leading-relaxed text-slate-500 dark:text-zinc-400">
                        {otpHelperText}
                      </p>

                      <OtpInputGroup
                        length={6}
                        value={controller.otpCode}
                        disabled={controller.isBusy}
                        onChange={controller.setOtpCode}
                      />

                      <div className={cn('h-5 text-center transition-opacity duration-300', controller.feedback?.tone === 'error' ? 'opacity-100' : 'opacity-0')}>
                        <p className="text-xs font-bold text-rose-500">{controller.feedback?.tone === 'error' ? controller.feedback.message : ' '}</p>
                      </div>

                      <div className="mt-2 text-center text-xs text-slate-500 dark:text-zinc-500">
                        {controller.state?.openChallengeRemainingAttempts != null ? (
                          <p>Intentos disponibles: {controller.state.openChallengeRemainingAttempts}</p>
                        ) : null}
                        {controller.rateLimitResetLabel ? (
                          <p className="mt-1">Ventana de envíos disponible nuevamente: {controller.rateLimitResetLabel}</p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {controller.verificationStage === 'success' ? (
                    <div className="mt-8 space-y-4 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_10px_30px_rgba(16,185,129,0.28)]">
                        <CheckCircle2 className="size-8" />
                      </div>
                      <p className="text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                        Ya puedes seguir con tu reserva. Esta validación quedará guardada para tus próximas visitas con esta cuenta.
                      </p>
                    </div>
                  ) : null}

                  {controller.verificationStage === 'otp' ? (
                    <div className="mb-4 mt-2 text-center">
                      {controller.canResend ? (
                        <button
                          type="button"
                          onClick={() => {
                            void (async () => {
                              try {
                                await controller.resendCode();
                                setDeliveryStartedInCurrentOpen(true);
                              } catch {
                                setDeliveryStartedInCurrentOpen(false);
                              }
                            })();
                          }}
                          disabled={controller.isBusy}
                          className="mx-auto inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-600 transition-colors hover:text-emerald-700"
                        >
                          {controller.resendMutationPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                          ¿No lo recibiste? Reenviar
                        </button>
                      ) : (
                        <p className="text-xs font-medium text-slate-400 dark:text-zinc-500">
                          Podrás reenviar el código en <span className="font-bold text-slate-700 dark:text-zinc-300">{formatCountdownLabel(controller.resendCountdownSeconds)}</span>
                        </p>
                      )}
                      {controller.resendAvailableLabel && !controller.canResend ? (
                        <p className="mt-2 text-[11px] text-slate-400 dark:text-zinc-600">Disponible nuevamente después de {controller.resendAvailableLabel}</p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="bg-white px-6 pb-8 dark:bg-black sm:px-10 sm:pb-10">
              <PrimaryButton
                label={primaryLabel}
                icon={ArrowRight}
                disabled={
                  controller.stateQuery.isLoading
                    ? true
                    : controller.stateQuery.isError
                      ? false
                      : controller.verificationStage === 'success'
                        ? false
                        : controller.verificationStage === 'otp'
                          ? !controller.canSubmitOtp
                          : !controller.canSubmitPhone
                }
                isLoading={controller.isBusy}
                isSuccess={showCelebrationState}
                onClick={() => {
                  if (controller.stateQuery.isError) {
                    void controller.stateQuery.refetch();
                    return;
                  }

                  if (controller.verificationStage === 'success') {
                    setIsOpen(false);
                    void onVerified?.();
                    return;
                  }

                  if (controller.verificationStage === 'otp') {
                    void controller.verifyCode();
                    return;
                  }

                  void (async () => {
                    try {
                      await controller.submitPhone();
                      setDeliveryStartedInCurrentOpen(true);
                    } catch {
                      setDeliveryStartedInCurrentOpen(false);
                    }
                  })();
                }}
              />
              {secondaryAction && controller.verificationStage !== 'success' ? (
                <button
                  type="button"
                  onClick={() => {
                    secondaryAction.onClick();
                    setIsOpen(false);
                  }}
                  disabled={controller.isBusy}
                  className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-full border border-slate-200 px-5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  {secondaryAction.label}
                </button>
              ) : null}
              <p className="mt-4 px-4 text-center text-[10px] leading-relaxed text-slate-400 dark:text-zinc-600">
                Al verificar aceptas nuestros términos de servicio y nuestra política de privacidad para gestionar tu solicitud de visita.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {showSummaryCard ? (
        <div className={cn('rounded-[1.8rem] border border-slate-200 bg-white p-5 shadow-[0_10px_24px_rgba(15,23,42,0.06)] dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-[0_18px_48px_rgba(0,0,0,0.45)]', className)}>
          <div className="flex items-start gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
              <ShieldCheck className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 dark:text-white">Protege tu cuenta para agendar más fácil</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                Validar tu WhatsApp una sola vez nos ayuda a cuidar tu cuenta y a confirmar tus próximas visitas con menos fricción.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-700 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300">
            <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-white">
              <Smartphone className="size-4" />
              Tu estado actual
            </div>
            <div className="mt-3 space-y-1 leading-relaxed">
              <p>WhatsApp confirmado: {controller.state?.securityPhoneVerifiedMasked ?? 'Aún no confirmado'}</p>
              <p>Verificación en curso: {controller.state?.securityPhonePendingMasked ?? 'Todavía no hay una verificación abierta'}</p>
              {controller.hasActiveOtpChallenge && controller.maskedDeliveryTarget ? (
                <p>Validación activa en: {controller.maskedDeliveryTarget}</p>
              ) : null}
            </div>
          </div>

          {controller.feedback ? (
            <div className="mt-4">
              <FeedbackBanner tone={controller.feedback.tone} message={controller.feedback.message} />
            </div>
          ) : null}

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                controller.clearFeedback();
                setDeliveryStartedInCurrentOpen(false);
                setIsOpen(true);
              }}
              className="inline-flex h-12 flex-1 items-center justify-center rounded-full bg-slate-900 px-5 text-sm font-medium text-white transition-colors hover:bg-slate-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200"
            >
              {controller.hasVerifiedPhone ? 'Ver confirmación' : controller.hasPendingPhone ? 'Retomar verificación' : 'Continuar con WhatsApp'}
            </button>
            <button
              type="button"
              onClick={() => void controller.stateQuery.refetch()}
              className="inline-flex h-12 items-center justify-center rounded-full border border-slate-200 px-5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-900"
            >
              Revisar de nuevo
            </button>
          </div>
        </div>
      ) : null}

      {typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent}
    </>
  );
}
